import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import CommandPalette from "./CommandPalette";
import { ContentSkeleton, skeletonVariantFor } from "./PageSkeleton";
import { RequireStaff } from "@/components/auth/AuthGuard";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import RouteErrorBoundary from "./RouteErrorBoundary";

const COLLAPSE_KEY = "sidebar-collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * The persistent admin shell. Sidebar, top bar and ⌘K stay mounted; only the
 * content well changes. A route change swaps the page at once and shows a
 * content-shaped skeleton (aria-busy) while the page's chunk loads.
 */
const ShellFrame = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, String(!c));
      } catch {
        /* ignore */
      }
      return !c;
    });
  }, []);

  // New page: start at the top of the content well and close the mobile sheet.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="fixed inset-0 flex overflow-hidden bg-background text-foreground">
      <a
        href="#admin-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-lg focus:bg-card focus:px-3 focus:py-2"
      >
        Skip to content
      </a>

      {!isMobile && <Sidebar collapsed={collapsed} onOpenSearch={() => setSearchOpen(true)} />}

      {isMobile && (
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="p-0 w-[260px] border-0">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Admin pages</SheetDescription>
            <Sidebar
              onOpenSearch={() => {
                setMobileNavOpen(false);
                setSearchOpen(true);
              }}
              onNavigate={() => setMobileNavOpen(false)}
            />
          </SheetContent>
        </Sheet>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          isMobile={isMobile}
          sidebarCollapsed={collapsed}
          onToggleSidebar={toggleCollapsed}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
        />
        <main id="admin-main" ref={mainRef} className="relative flex-1 overflow-y-auto" tabIndex={-1}>
          <RouteErrorBoundary resetKey={location.pathname}>
            <Suspense
              fallback={
                <div className="px-4 py-6 md:px-6 lg:px-8 lg:py-8">
                  <ContentSkeleton variant={skeletonVariantFor(location.pathname)} />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
};

const AdminShell = () => (
  <RequireStaff>
    <ShellFrame />
  </RequireStaff>
);

export default AdminShell;
